"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, MapPin, Code as Code2, ShoppingCart, ArrowRight, Sparkles, ChartLine as LineChart, Search, Cpu, UserCheck, CircleCheck as CheckCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function Explainer() {
  const shouldReduceMotion = useReducedMotion();
  const coreServices = [{
    title: "Technical SEO & Infrastructure",
    path: "/TechnicalSeoPage",
    badge: "Architecture & Performance",
    icon: Code2,
    description: "I diagnose and eliminate the crawling, indexing, and rendering barriers that silently suppress your organic visibility. You get direct code-level fixes, not generic tool exports.",
    highlights: ["Server response time & Core Web Vitals optimization", "JavaScript rendering & schema markup architecture", "Crawl budget consolidation & indexation control", "Site migration management without traffic drops"],
    linkText: "Explore Technical SEO"
  }, {
    title: "Local SEO & Multi-Location Growth",
    path: "/LocalSEOPage",
    badge: "Hyper-Targeted Reach",
    icon: MapPin,
    description: "I position your local branches and service hubs directly into Google Map Packs and geo-targeted search results, attracting high-intent customers in your territory.",
    highlights: ["Google Business Profile optimization and auditing", "Local citation building and geo-landing pages", "Location-based keyword research and competitor tracking", "Reputation management and review strategy"],
    linkText: "Explore Local SEO"
  }, {
    title: "Ecommerce SEO & Revenue Scaling",
    path: "/EcommerceSeoPage",
    badge: "High-Intent Commercial",
    icon: ShoppingCart,
    description: "I structure your category hierarchy, refine faceted navigation, and optimize product visibility to capture ready-to-buy shoppers across competitive retail niches.",
    highlights: ["Product taxonomy and faceted navigation indexing", "Transactional keyword capture & search intent matching", "Internal linking architecture for product clusters", "Competitor category gap & pricing intent analysis"],
    linkText: "Explore Ecommerce SEO"
  }];
  const valueProps = [{
    title: "Direct Specialist Access",
    desc: "Every audit, strategy, and execution step is handled personally by me. No junior account managers or generic templates.",
    icon: UserCheck
  }, {
    title: "Execution-First Strategy",
    desc: "I deliver concrete fixes, customized roadmaps, and ready-to-deploy code rather than hundred-page theoretical decks.",
    icon: Cpu
  }, {
    title: "Transparent, Honest Growth",
    desc: "Regular, plain-English updates and custom dashboard reporting focused on qualified organic pipeline and real revenue.",
    icon: LineChart
  }];
  const freelancerActivities = ["SEO audits", "Keyword research", "Technical SEO", "On-page optimization", "Local SEO", "E-commerce SEO", "B2B SEO", "Link building", "Performance monitoring (GA/Search Console)"];
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicesexplainer" data-nav="light" className="bg-background py-20 md:py-28 text-foreground font-sans relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="bg-secondary text-primary font-medium px-3.5 py-1 text-xs md:text-sm rounded-full mb-4 inline-flex items-center gap-1.5 shadow-2xs border border-accent/20">
            <Sparkles className="size-3.5 text-accent" />
            <span>Hands-On Consultancy Capabilities</span>
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-primary font-serif mb-6">
            How I Build Sustainable Organic Search Advantage
          </h2>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            I work as an independent extension of your business. Rather than juggling dozens of low-touch accounts like an agency, I partner closely with select clients to engineer deep technical foundations, targeted local dominance, and measurable commercial search growth.
          </p>
        </div>

        {/* What Does an SEO Freelancer in Dubai Do? */}
        <div className="bg-card border border-border/80 rounded-2xl p-8 md:p-12 mb-16 shadow-xs">
          <div className="max-w-3xl mb-8">
            <h3 className="text-xl sm:text-2xl font-bold text-primary font-serif mb-4">
              What Does an SEO Freelancer in Dubai Do?
            </h3>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              An SEO freelancer covers technical optimization, keyword research, content strategy, and link building. Unlike agencies, you work directly with me, Yassine.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 md:gap-4 mb-8">
            {freelancerActivities.map((activity, idx) => <div key={activity} data-index={idx} className="flex items-center gap-3 p-3.5 rounded-lg bg-background border border-border/70 hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                <div className="size-6 rounded-md bg-accent/15 text-accent flex items-center justify-center shrink-0">
                  <CheckCircle className="size-4" />
                </div>
                <span className="text-sm md:text-base font-medium text-foreground">
                  {activity}
                </span>
              </div>)}
          </div>

          <div className="border-t border-border/70 pt-6">
            <p className="text-sm md:text-base text-foreground/90 font-medium leading-relaxed">
              You communicate directly with me on strategy and results — no account-manager layers or delays. I provide transparent milestone reporting so you always know where your search visibility stands.
            </p>
          </div>
        </div>

        {/* 3 Main Capability Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-80px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {coreServices.map((service, i) => {
          const IconComponent = service.icon;
          return <motion.div key={service.title} data-index={i} variants={itemVariants} className="h-full flex">
                <Card className="flex flex-col justify-between w-full border border-border/80 bg-card hover:border-primary/40 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-300 rounded-xl overflow-hidden group">
                  <div>
                    <CardHeader className="space-y-3 pb-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-[color,background-color] duration-300">
                          <IconComponent className="size-6" />
                        </div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-accent bg-accent/10 px-2.5 py-1 rounded-full">
                          {service.badge}
                        </span>
                      </div>
                      
                      <CardTitle className="text-xl md:text-2xl font-bold text-primary pt-2 group-hover:text-primary transition-colors">
                        {service.title}
                      </CardTitle>
                      
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {service.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-2 pb-6">
                      <Separator className="mb-4 bg-border/60" />
                      <div className="space-y-2.5">
                        <p className="text-xs font-semibold tracking-wider uppercase text-foreground/80">Key Deliverables:</p>
                        {service.highlights.map((item, itemIndex) => <div key={itemIndex} data-index={itemIndex} className="flex items-start gap-2.5 text-xs md:text-sm text-foreground/90">
                            <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </div>)}
                      </div>
                    </CardContent>
                  </div>

                  <div className="px-6 pb-6 pt-2">
                    <Button asChild variant="outline" className="w-full justify-between group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-[color,background-color,border-color] duration-300">
                      <Link to={service.path} className="flex items-center justify-between w-full">
                        <span>{service.linkText}</span>
                        <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Why Working Directly With Me Matters */}
        <div className="bg-secondary/40 border border-border rounded-2xl p-8 md:p-12 mb-16 shadow-2xs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            <div className="lg:col-span-5 space-y-4">
              <Badge variant="outline" className="text-xs font-medium border-accent/40 text-foreground bg-background">
                My Working Principles
              </Badge>
              <h3 className="text-2xl md:text-3xl font-bold text-primary font-serif">
                You Work Directly With Me, Not An Intermediary
              </h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                When you hire YE Consultancy, you get my direct strategic attention. I personally analyze your site architecture, run competitive intelligence, and write implementation plans that fit your tech stack.
              </p>
              <div className="pt-2">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm">
                  <Link to="/ContactPage" className="flex items-center gap-2">
                    <span>Discuss Your Project</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {valueProps.map((item, idx) => {
              const Icon = item.icon;
              return <div key={item.title} data-index={idx} className="bg-card border border-border/80 p-5 rounded-xl shadow-xs flex flex-col justify-between">
                    <div>
                      <div className="size-10 rounded-lg bg-accent/15 text-accent flex items-center justify-center mb-3.5">
                        <Icon className="size-5" />
                      </div>
                      <h4 className="font-semibold text-foreground text-sm md:text-base mb-1.5">
                        {item.title}
                      </h4>
                      <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

          </div>
        </div>

        {/* Quick Audit / Consultation Banner */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 border-t border-border pt-8">
          <div className="flex items-center gap-3.5">
            <div className="size-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <Search className="size-5" />
            </div>
            <div>
              <p className="font-semibold text-foreground text-sm md:text-base">
                Looking for a bespoke SEO audit or full strategy review?
              </p>
              <p className="text-xs md:text-sm text-muted-foreground">
                I identify what is holding your search performance back before proposing a retainer.
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <Button asChild variant="default" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/SeoAuditsPage">Explore SEO Audits</Link>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto border-border hover:bg-accent hover:text-accent-foreground">
              <Link to="/CaseStudiesPage">View Verified Case Studies</Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}